import { createSignupClickHandler } from './signupClickTracking.js';
import { createBadderswebArrivalCapture } from './badderswebTracking.js';
import posthog from 'posthog-js/dist/module.slim';
import { createTestClubClickHandler } from './testClubClickTracking.js';
import {
  getRouteProperties,
  POSTHOG_KEY,
  POSTHOG_OPTIONS,
  VERIFICATION_STORAGE_KEY,
} from './analyticsConfig';

let initialized = false;
let lastPageviewPathname = null;
const captureBadderswebArrival = createBadderswebArrivalCapture(
  (event, properties) => posthog.capture(event, properties, { send_instantly: true }),
);

const getVerificationMarker = () => {
  try {
    const marker = window.sessionStorage.getItem(VERIFICATION_STORAGE_KEY);
    return /^tin_verify_[a-z0-9_-]{1,48}$/i.test(marker || '') ? marker : null;
  } catch {
    return null;
  }
};

const getEventProperties = (pathname) => {
  const routeProperties = getRouteProperties(pathname);
  if (!routeProperties) {
    return null;
  }

  const verificationMarker = getVerificationMarker();
  return {
    ...routeProperties,
    source: 'public_site',
    ...(verificationMarker ? { verification_marker: verificationMarker } : {}),
  };
};

export const initializeAnalytics = () => {
  if (initialized) {
    return;
  }

  posthog.init(POSTHOG_KEY, {
    ...POSTHOG_OPTIONS,
    // Marked verification sessions stay readable in the browser network panel.
    disable_compression: Boolean(getVerificationMarker()),
  });
  initialized = true;
  captureBadderswebArrival(window.location, getVerificationMarker());
  // Capture before React changes the route. Register once, outside component renders.
  const captureTestClubClick = createTestClubClickHandler(
    () => window.location.pathname,
    (properties) => posthog.capture(
      properties.pathname === '/test-club' ? 'test_club_app_cta_clicked' : 'test_club_cta_clicked', {
        ...getEventProperties(properties.pathname),
        ...properties,
      }, { send_instantly: true, transport: 'sendBeacon' },
    ),
  );
  document.addEventListener('click', captureTestClubClick, true);
  document.addEventListener('auxclick', captureTestClubClick, true);
  const captureSignupClick = createSignupClickHandler(
    () => window.location.pathname,
    (properties) => posthog.capture('signup_cta_clicked', {
      ...getEventProperties(window.location.pathname),
      ...properties,
    }, { send_instantly: true, transport: 'sendBeacon' }),
  );
  document.addEventListener('click', captureSignupClick, true);
  document.addEventListener('auxclick', captureSignupClick, true);
};

export const capturePageview = (pathname) => {
  const properties = getEventProperties(pathname);
  if (!properties || properties.pathname === lastPageviewPathname) {
    return;
  }

  lastPageviewPathname = properties.pathname;
  posthog.capture('$pageview', properties);
};

export const captureDemoModalOpened = (pathname, placement) => {
  const properties = getEventProperties(pathname);
  if (!properties) {
    return;
  }

  posthog.capture('demo_modal_opened', {
    ...properties,
    placement,
  });
};

export const captureFaqExpanded = (pathname, questionKey) => {
  const properties = getEventProperties(pathname);
  if (!properties) {
    return;
  }

  posthog.capture('faq_expanded', {
    ...properties,
    question_key: questionKey,
  });
};
